import React, { useState } from 'react';
import { Form, Input, Select, DatePicker, Button, message, Result } from 'antd';
import { PlusOutlined, DeleteOutlined, MailOutlined } from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import api from '../api/client';

const STUDENT_EDU_LEVELS = ['Primary', 'Secondary', 'Post-Secondary'];

function ResendButton({ email }) {
  const [loading, setLoading] = useState(false);
  const [sent, setSent]       = useState(false);

  const resend = async () => {
    setLoading(true);
    try {
      await api.post('/api/auth/resend-verification', { email });
      setSent(true);
    } catch {}
    finally { setLoading(false); }
  };

  if (sent) return <p style={{ color: '#52c41a', fontSize: 13 }}>✓ Verification email resent</p>;
  return (
    <Button loading={loading} onClick={resend} style={{ fontSize: 13 }}>
      Resend verification email
    </Button>
  );
}
const TUTOR_EDU_LEVELS   = ['PSLE', 'O-Level', 'IP', 'JC', 'IB', 'University', 'Other'];

const CURRENT_LEVEL_HINTS = {
  Primary: 'e.g. P1, P2, P3, P4, P5, P6',
  Secondary: 'e.g. Sec 1, Sec 2, Sec 3, Sec 4, Sec 5',
  'Post-Secondary': 'e.g. JC1, JC2, Poly Y1, ITE Y1',
};

export default function Register() {
  const [loading, setLoading]       = useState(false);
  const [role, setRole]             = useState('');
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const isStudent = role === 'student';
  const eduLevels = isStudent ? STUDENT_EDU_LEVELS : TUTOR_EDU_LEVELS;

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const education = (values.education || []).filter(e => e?.school?.trim() && e?.level);
      const res = await api.post('/api/auth/register', {
        ...values,
        date_of_birth: values.date_of_birth.format('YYYY-MM-DD'),
        education,
      });
      if (res.data.requiresVerification) {
        setRegisteredEmail(values.email);
      } else {
        message.success('Account created — sign in now');
        navigate('/');
      }
    } catch (e) {
      message.error(e.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // ── Check-your-email screen ────────────────────────────────────────────────
  if (registeredEmail) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa', padding: '40px 24px' }}>
        <div style={{ width: 440, background: '#fff', border: '1px solid #e8e8e8', borderRadius: 12, padding: 48, textAlign: 'center' }}>
          <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 32 }}>SGLearner</div>
          <MailOutlined style={{ fontSize: 52, color: '#5470c6', marginBottom: 16 }} />
          <h2 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 8px' }}>Check your email</h2>
          <p style={{ color: '#888', marginBottom: 8 }}>
            We sent a verification link to <strong>{registeredEmail}</strong>.
          </p>
          <p style={{ color: '#aaa', fontSize: 13, marginBottom: 28 }}>
            Click the link in the email to activate your account. It expires in 24 hours.
          </p>
          <ResendButton email={registeredEmail} />
          <div style={{ marginTop: 20, fontSize: 13, color: '#888' }}>
            <Link to="/" style={{ color: '#0a0a0a' }}>Back to login</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa', padding: '40px 24px' }}>
      <div style={{ width: 500 }}>
        <div style={{ marginBottom: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.5px' }}>SGLearner</div>
          <div style={{ fontSize: 13, color: '#888', marginTop: 6 }}>Create your account</div>
        </div>

        <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: 10, padding: 32 }}>
          <Form form={form} layout="vertical" onFinish={onFinish} requiredMark={false}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              <Form.Item label="Full name" name="name" rules={[{ required: true }]}>
                <Input size="large" placeholder="Jane Tan" />
              </Form.Item>
              <Form.Item label="Username" name="username" rules={[{ required: true }]}>
                <Input size="large" placeholder="janetan" />
              </Form.Item>
            </div>
            <Form.Item label="Email" name="email" rules={[{ required: true, type: 'email' }]}>
              <Input size="large" placeholder="you@email.com" />
            </Form.Item>
            <Form.Item label="Password" name="password" rules={[{ required: true, min: 8 }]}>
              <Input.Password size="large" placeholder="••••••••" />
            </Form.Item>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              <Form.Item label="Date of birth" name="date_of_birth" rules={[{ required: true }]}>
                <DatePicker size="large" style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item label="Gender" name="gender" rules={[{ required: true }]}>
                <Select size="large" placeholder="Select">
                  <Select.Option value="Male">Male</Select.Option>
                  <Select.Option value="Female">Female</Select.Option>
                  <Select.Option value="Other">Other</Select.Option>
                </Select>
              </Form.Item>
            </div>

            <Form.Item label="I am a" name="role_name" rules={[{ required: true }]}>
              <Select size="large" placeholder="Select role"
                onChange={v => { setRole(v); form.setFieldValue('education', []); form.setFieldValue('current_level', undefined); }}>
                <Select.Option value="student">Student</Select.Option>
                <Select.Option value="tutor">Tutor</Select.Option>
              </Select>
            </Form.Item>

            {/* Student: current level */}
            {isStudent && (
              <Form.Item label="Current school year" name="current_level"
                help="e.g. P6, Sec 3, JC1">
                <Input size="large" placeholder="e.g. P6, Sec 3, JC1" />
              </Form.Item>
            )}

            {/* Education background */}
            {role && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, color: '#555', fontWeight: 500, marginBottom: 8 }}>
                  {isStudent ? 'School' : 'Education background'}
                  <span style={{ color: '#aaa', fontWeight: 400 }}> (optional)</span>
                </div>
                <Form.List name="education">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name }) => (
                        <div key={key} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                          <Form.Item name={[name, 'school']} style={{ flex: 1, marginBottom: 0 }}
                            rules={[{ required: true, message: 'Enter school name' }]}>
                            <Input size="large" placeholder={isStudent ? 'School name' : 'School / University'} />
                          </Form.Item>
                          <Form.Item name={[name, 'level']} style={{ width: 150, marginBottom: 0 }}
                            rules={[{ required: true, message: 'Select level' }]}>
                            <Select size="large" placeholder="Level">
                              {eduLevels.map(l => <Select.Option key={l} value={l}>{l}</Select.Option>)}
                            </Select>
                          </Form.Item>
                          <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)} style={{ marginTop: 4 }} />
                        </div>
                      ))}
                      <Button type="dashed" icon={<PlusOutlined />} onClick={() => add()} size="small" block>
                        {isStudent ? 'Add school' : 'Add education'}
                      </Button>
                    </>
                  )}
                </Form.List>
              </div>
            )}

            <Form.Item label="Bio" name="description">
              <Input.TextArea rows={3} placeholder={isStudent ? 'Tell tutors about yourself...' : 'Tell students about yourself...'} />
            </Form.Item>

            <Form.Item style={{ marginBottom: 12 }}>
              <Button type="primary" htmlType="submit" block size="large" loading={loading}
                style={{ background: '#0a0a0a', border: 'none', fontWeight: 500 }}>
                Create account
              </Button>
            </Form.Item>
          </Form>
          <div style={{ textAlign: 'center', fontSize: 13, color: '#666' }}>
            Already have an account? <Link to="/" style={{ color: '#0a0a0a', fontWeight: 500 }}>Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
